// frontend/src/features/import/utils/lock-route.ts
import type { Point } from './orthogonal-router'

/**
 * Derives the lockable interior waypoint chain from a rendered pipe polyline.
 *
 * "Keep this route" and `lockAllPipeRoutes` persist a pipe's current bends into
 * `diagram.waypoints` so the router threads them verbatim across device moves
 * (instead of re-running the fold-prone auto L/Z router). This helper turns the
 * live `diagram.points` polyline into a valid waypoint chain, or returns
 * `undefined` when there is nothing lockable.
 *
 * The output MUST equal the interior of `points` verbatim:
 *  - `setEdgeWaypoints` only accepts an orthogonal chain (consecutive corners
 *    aligned in x or y within EPS); a diagonal segment is unrepresentable.
 *  - `slideSegment` locates the chain as an exact contiguous subsequence of the
 *    render polyline's interior, so we must NOT collapse collinear points.
 *
 * `points` conventions differ by source and both are handled by a position-based
 * endpoint strip:
 *  - desktop-JSON `diagram.points` is endpoint-INCLUSIVE (first ≈ src, last ≈ tgt);
 *  - router-generated / `.pipe` `points` are interior-only.
 *
 * `srcPos`/`tgtPos` are OPTIONAL: a missing endpoint just means "don't strip that
 * end" — never dereference undefined. Returns `undefined` (no throw) for empty,
 * straight, degenerate, non-finite, or non-orthogonal input.
 */
export function deriveLockableWaypoints(
  points: readonly Point[] | undefined,
  srcPos?: Point,
  tgtPos?: Point,
): Point[] | undefined {
  if (!points || points.length === 0) return undefined
  if (!points.every((p) => Number.isFinite(p.x) && Number.isFinite(p.y))) return undefined

  const EPS = 0.01
  const approxEqual = (a: Point, b: Point): boolean =>
    Math.abs(a.x - b.x) < EPS && Math.abs(a.y - b.y) < EPS

  let interior = points.slice()
  // Position-based endpoint strip (optional endpoints — guard each dereference).
  if (srcPos !== undefined && interior.length > 0 && approxEqual(interior[0]!, srcPos)) {
    interior = interior.slice(1)
  }
  if (tgtPos !== undefined && interior.length > 0 && approxEqual(interior[interior.length - 1]!, tgtPos)) {
    interior = interior.slice(0, -1)
  }

  // Nothing left → a straight run (no corners); it doesn't fold, so nothing to lock.
  if (interior.length < 1) return undefined

  // Consecutive interior corners must be orthogonal (matches setEdgeWaypoints).
  for (let i = 1; i < interior.length; i++) {
    const p = interior[i]!
    const q = interior[i - 1]!
    if (!(Math.abs(p.x - q.x) < EPS || Math.abs(p.y - q.y) < EPS)) return undefined
  }

  // No collinear collapse — return the interior verbatim (slideSegment invariant).
  return interior
}
